import { useState, useCallback, useMemo } from "react";
import { previewOwnershipCostAttribution } from "@/helpers/apis";
import { OwnershipCostPreviewResponse } from "./types";
import { useDebounce } from "@/helpers/hooks";
import { OwnershipRulesFormValues } from "./types";
import { buildOwnershipRequests } from "./ownershipRequestBuilder";

export const useOwnershipCostPreview = (
  watchedOwnerships: OwnershipRulesFormValues["ownerships"]
) => {
  const debouncedOwnerships = useDebounce(watchedOwnerships, 400);
  const ownershipRequests = useMemo(
    () => buildOwnershipRequests(debouncedOwnerships),
    [debouncedOwnerships]
  );
  const hasPreviewableOwnerships = ownershipRequests.some(request =>
    request.ownership_rules.some(
      rule => rule.condition_value?.trim() || rule.condition_key_value?.trim()
    )
  );
  const [costPreview, setCostPreview] = useState<OwnershipCostPreviewResponse | null>(null);
  const [isCostPreviewLoading, setIsCostPreviewLoading] = useState(false);
  const [costPreviewError, setCostPreviewError] = useState<string | null>(null);

  // Calculate date range for the last 28 days
  const dateRange = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 28);
    return {
      start_date: startDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0],
    };
  }, []);

  const refreshCostPreview = useCallback(
    async (requestsToUse?: typeof ownershipRequests) => {
      const requests = requestsToUse || ownershipRequests;
      const hasPreviewable = requests.some(request =>
        request.ownership_rules.some(
          rule => rule.condition_value?.trim() || rule.condition_key_value?.trim()
        )
      );

      if (!hasPreviewable) {
        setCostPreview(null);
        setCostPreviewError(null);
        return;
      }

      setIsCostPreviewLoading(true);
      setCostPreviewError(null);
      try {
        const response = await previewOwnershipCostAttribution(
          {
            ownership_requests: requests,
          },
          dateRange
        );
        setCostPreview(response ?? null);
      } catch (error) {
        console.error(error);
        setCostPreview(null);
        setCostPreviewError("Unable to refresh cost attribution right now.");
      } finally {
        setIsCostPreviewLoading(false);
      }
    },
    [ownershipRequests, dateRange]
  );

  return {
    costPreview,
    isCostPreviewLoading,
    costPreviewError,
    hasPreviewableOwnerships,
    refreshCostPreview,
    ownershipRequests,
    dateRange,
  };
};
